import {
  authoredLocaleOf,
  isAutoTranslated,
  type Draft,
} from "@heraion/shared";
import type { SupportedLng } from "@heraion/i18n";

/** What the program's header says ABOUT the program, as opposed to what the
 *  coach types into it. Shape and derivation both lifted verbatim out of the
 *  deleted ComposerMetaBand (PE-39/K5) — the classic shell carried these four
 *  facts and the prescribe tree carried none of them. */
export type PrescribeHeaderMeta = {
  /** PE-21 — the text being edited was written by a machine, not by the coach.
   *  ⛔ Not cosmetic: without it a coach cannot tell their own words from a
   *  model's, and a later regeneration overwrites them silently. The 2026-08-22
   *  backfill stamped `autoTranslated` on all 15 production programs, four
   *  languages each, so this renders on almost every existing program. */
  machineWritten: boolean;
  /** The language the program was actually authored in, when the editor is
   *  showing a language nobody has written yet. Switching there empties every
   *  field — correct (a fallback would let the coach type over the original)
   *  but it reads exactly like data loss, so it gets said out loud. */
  untranslatedFrom: SupportedLng | null;
  editLocale: SupportedLng | null;
  weekCount: number;
  dayCount: number;
};

/** ⛔ `declaredAuthorLocale` is passed, never omitted: without it
 *  `authoredLocaleOf` picks the first language in supported-locale order, and a
 *  program already written in two languages would name English as "the
 *  original" while the original is Italian. */
export const prescribeHeaderMeta = (draft: Draft): PrescribeHeaderMeta => ({
  machineWritten:
    draft.editLocale !== null &&
    draft.editLocale !== undefined &&
    isAutoTranslated(draft.nameRaw, draft.editLocale),
  untranslatedFrom:
    draft.editLocale && draft.name.trim().length === 0
      ? authoredLocaleOf(draft.nameRaw, draft.declaredAuthorLocale)
      : null,
  editLocale: draft.editLocale ?? null,
  weekCount: draft.cycleWeeks,
  dayCount: draft.sessions.length,
});
